import React from 'react';
import {
  getRatingDisplayConfig,
  getRatingSizeClasses,
  getRatingVariantClasses,
  getStarMessageWithCount,
  getScoreColor,
  shouldScoreBeBold,
  parseLearnosityScoresIntoStars,
  RatingDisplayOptions,
} from '../../utils/skillHistoryUtils';
import FilledIceCube from '../Dashboard/DashboardIcons/FilledIceCube';
import EmptyIceCube from '../Dashboard/DashboardIcons/EmptyIceCube';

/**
 * Interface for RatingDisplay component props
 */
export interface RatingDisplayProps {
  score: number;
  maxScore?: number | null;
  className?: string;
  options?: RatingDisplayOptions;
}

/**
 * Reusable RatingDisplay component that can be used across all components
 * This component handles all the rating display logic and can be configured
 * for different use cases (simple, detailed, compact)
 */
export const RatingDisplay: React.FC<RatingDisplayProps> = ({
  score,
  maxScore,
  className = '',
  options = {
    showStars: true,
    showMessage: true,
  },
}) => {
  const config = getRatingDisplayConfig(options);
  const sizeClasses = getRatingSizeClasses(config.size);
  const variantClasses = getRatingVariantClasses(config.variant);

  // Don't render if no score
  if (!score && score !== 0) return null;

  const starInfo = config.showMessage ? getStarMessageWithCount(score, maxScore) : null;
  const scoreColor = getScoreColor(score, maxScore);
  const shouldBold = shouldScoreBeBold(score, maxScore);

  return (
    <div className={`nc_result-ratings ${variantClasses} ${sizeClasses} ${className}`}>
      {config.showStars && (
        <div className="tw-flex tw-items-center !tw-flex-row tw-gap-1">
          {Array.from({ length: 5 }).map((_, i) => {
            const filledCount = parseLearnosityScoresIntoStars(score, maxScore);
            if (i < filledCount) {
              return <FilledIceCube key={i} size={config.size === 'small' ? 13 : 14} />;
            }
            return <EmptyIceCube key={i} size={config.size === 'small' ? 13 : 14} isSmall={true} />;
          })}
        </div>
      )}

      {config.showMessage && starInfo && (
        <span
          className={`tw-font-normal tw-text-sm tw-font-atkinson tw-line-clamp-1 ${scoreColor} ${
            shouldBold ? '!tw-font-bold' : ''
          }`}
        >
          {starInfo.message}
        </span>
      )}
    </div>
  );
};

/**
 * Pre-configured rating display components for common use cases
 */

/**
 * Compact rating display with stars and message
 */
export const CompactRatingDisplay: React.FC<Omit<RatingDisplayProps, 'options'>> = props => (
  <RatingDisplay
    {...props}
    options={{
      showStars: true,
      showMessage: true,
      variant: 'compact',
      size: 'small',
    }}
  />
);

/**
 * Dashboard card rating display (matches current DashboardCard implementation)
 */
export const DashboardRatingDisplay: React.FC<Omit<RatingDisplayProps, 'options'>> = props => (
  <RatingDisplay
    {...props}
    options={{
      showStars: true,
      showMessage: false,
      variant: 'detailed',
      size: 'medium',
    }}
  />
);

/**
 * Support skill rating display (matches current AboutTheSkill implementation)
 */
export const SupportSkillRatingDisplay: React.FC<Omit<RatingDisplayProps, 'options'>> = props => (
  <RatingDisplay
    {...props}
    options={{
      showStars: true,
      showMessage: true,
      variant: 'detailed',
      size: 'medium',
    }}
  />
);
